import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { BUDGET_PERIOD_PRESETS, type BudgetPeriodState } from "../hooks/useBudgetPeriod";
import { scopePeriods } from "../lib/budgetTimeline";
import { monthLabel } from "../lib/format";
import { ui } from "../theme";

// Filtro de periodo de Presupuestos (#1332): «desde – hasta» de meses completos,
// arriba de la página. Todo lo que hay debajo se calcula con este periodo.

export default function BudgetPeriodFilter({ period }: { period: BudgetPeriodState }) {
  const { t, i18n } = useTranslation(["common", "expensesStatements"]);
  const tf = (k: string, o?: Record<string, unknown>) => t(`expensesStatements:budgetExec.filter.${k}`, o);
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const { range, draft } = period;
  const months = scopePeriods(range).length;
  const label = range.from === range.to ? monthLabel(range.from, i18n.language) : `${monthLabel(range.from, i18n.language)} – ${monthLabel(range.to, i18n.language)}`;

  return (
    <div className={`${c.card} flex flex-wrap items-end gap-x-4 gap-y-3`} data-testid="budget-period-filter">
      <div className="min-w-[10rem]">
        <p className={`text-xs font-semibold uppercase tracking-wide ${c.muted}`}>
          <i className="ri-calendar-2-line mr-1 text-teal-600" />
          {tf("title")}
        </p>
        <p className={`text-sm font-semibold ${c.text}`} data-testid="budget-period-label">
          {label}
        </p>
        <p className={`text-xs ${c.muted}`}>{tf("months", { count: months })}</p>
      </div>
      <label className={`text-xs ${c.muted}`}>
        <span className="mb-1 block">{t("expenses.summary.from")}</span>
        <input
          type="month"
          aria-label={t("expenses.summary.from")}
          className={`${c.inputBase} w-auto`}
          placeholder={t("expenses.summary.monthPlaceholder")}
          value={draft.from}
          onChange={(e) => period.change({ from: e.target.value })}
          onBlur={period.resetDraft}
        />
      </label>
      <label className={`text-xs ${c.muted}`}>
        <span className="mb-1 block">{t("expenses.summary.to")}</span>
        <input
          type="month"
          aria-label={t("expenses.summary.to")}
          className={`${c.inputBase} w-auto`}
          placeholder={t("expenses.summary.monthPlaceholder")}
          value={draft.to}
          onChange={(e) => period.change({ to: e.target.value })}
          onBlur={period.resetDraft}
        />
      </label>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={tf("presets")}>
        {BUDGET_PERIOD_PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            aria-pressed={period.preset === p}
            data-testid={`preset-${p}`}
            onClick={() => period.usePreset(p)}
            className={`rounded-full border px-3 py-1 text-xs ${period.preset === p ? "border-teal-600 bg-teal-600 text-white" : `${c.border} ${c.muted} hover:border-teal-600 hover:text-teal-600`}`}
          >
            {tf(`preset.${p}`)}
          </button>
        ))}
      </div>
      <p className={`basis-full text-xs ${c.muted}`}>{tf("hint")}</p>
    </div>
  );
}
